// Settings → Company (Wave E).
//
// Mirrors `tricivo_system/operations/company_settings_management.php`:
// 4 tabs — Info / Branding / Timeframes / Reminders. Per CLIENT_DECISIONS,
// theme picker is a separate page (/settings/theme); this page handles the
// company profile + reminders and the operating-hours / timeframe text
// fields. Only admins can save (server enforces).

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Tabs } from '../../../components/ui/Tabs/Tabs';
import { useCompanyOverview, useUpdateCompany } from './hooks/useCompany';
import { useNavigate } from 'react-router-dom';
import type { UpdateCompanyInput } from '../../../types/api';
import { EmailAccountCard } from './components/EmailAccountCard';
import { SmsConfigCard } from './components/SmsConfigCard';
import { NotificationPreferencesGrid } from './components/NotificationPreferencesGrid';
import { ImportParsersTable } from './components/ImportParsersTable';
import styles from './CompanySettingsPage.module.css';

type TabId =
  | 'info'
  | 'branding'
  | 'timeframes'
  | 'reminders'
  | 'email-sms';

export function CompanySettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const overview = useCompanyOverview();
  const update = useUpdateCompany();
  // The not-configured 404 response from the import endpoint links here
  // with `#email-import`, so on first mount jump straight to the new tab
  // and scroll the import section into view.
  const initialTab: TabId =
    typeof window !== 'undefined' && window.location.hash === '#email-import'
      ? 'email-sms'
      : 'info';
  const [tab, setTab] = useState<TabId>(initialTab);
  const importSectionRef = useRef<HTMLHeadingElement | null>(null);

  // Form state lives one bag — we send a partial PATCH on save.
  const [form, setForm] = useState<UpdateCompanyInput>({});
  // Snapshot of the form as initially loaded from the server. On submit we
  // only PATCH fields whose value has actually changed — re-sending unchanged
  // pre-existing values can be rejected by the strict PATCH schema (e.g. a
  // legacy phone number that doesn't match the canonical UK regex).
  const originalRef = useRef<UpdateCompanyInput>({});

  // Scroll to the import section when arriving via #email-import hash.
  useEffect(() => {
    if (tab !== 'email-sms') return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#email-import') return;
    // Wait one tick for the section to render.
    const t = window.setTimeout(() => {
      importSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [tab]);

  useEffect(() => {
    if (!overview.data) return;
    const c = overview.data.company;
    const initial: UpdateCompanyInput = {
      name: c.name,
      email: c.email,
      phone: c.phone,
      address: c.address,
      postcode: c.postcode,
      website: c.website,
      whatsapp: c.whatsapp,
      logo_url: c.logo_url,
      primary_color: c.primary_color,
      secondary_color: c.secondary_color,
      bank_name: c.bank_name,
      bank_sort_code: c.bank_sort_code,
      bank_account_number: c.bank_account_number,
      bank_account_name: c.bank_account_name,
      operating_hours_weekdays: c.operating_hours_weekdays,
      operating_hours_weekends: c.operating_hours_weekends,
      timeframe_am_weekday: c.timeframe_am_weekday,
      timeframe_am_saturday: c.timeframe_am_saturday,
      timeframe_ampm_weekday: c.timeframe_ampm_weekday,
      timeframe_ampm_saturday: c.timeframe_ampm_saturday,
      timeframe_pm_weekday: c.timeframe_pm_weekday,
      timeframe_pm_saturday: c.timeframe_pm_saturday,
      automated_email_reminder_enabled: c.automated_email_reminder_enabled,
      automated_email_reminder_hours: c.automated_email_reminder_hours,
      automated_email_reminder_time: c.automated_email_reminder_time,
      automated_sms_reminder_enabled: c.automated_sms_reminder_enabled,
      automated_sms_reminder_hours: c.automated_sms_reminder_hours,
      automated_sms_reminder_time: c.automated_sms_reminder_time,
    };
    setForm(initial);
    originalRef.current = initial;
  }, [overview.data]);

  if (overview.isLoading || !overview.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Company Settings" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  function setField<K extends keyof UpdateCompanyInput>(key: K, value: UpdateCompanyInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const original = originalRef.current;
    const diff: UpdateCompanyInput = {};
    for (const k of Object.keys(form) as Array<keyof UpdateCompanyInput>) {
      if (form[k] !== original[k]) {
        // Type system can't see that form[k] and diff[k] share a key here,
        // but at runtime we're copying value-by-value within the same shape.
        (diff as Record<string, unknown>)[k] = form[k];
      }
    }
    update.mutate(diff);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Company Settings"
        subtitle="Update your company profile, branding, timeframes, and reminders"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(id) => setTab(id as TabId)}
        items={[
          { id: 'info', label: 'Company Info' },
          { id: 'branding', label: 'Branding' },
          { id: 'timeframes', label: 'Collection Timeframes' },
          { id: 'reminders', label: 'Automated Reminders' },
          { id: 'email-sms', label: 'Email & SMS' },
        ]}
      />

      {tab === 'email-sms' ? (
        <div className={styles.commsSections}>
          <h3 className={styles.sectionHead}>Outbound Email Accounts</h3>
          <EmailAccountCard
            purpose="notifications"
            title="Notifications Email"
            description="Used for booking confirmations, reminders, and general customer notifications."
          />
          <EmailAccountCard
            purpose="receipts"
            title="Receipts Email"
            description="Used when sending COVD receipt PDFs to customers."
          />
          <EmailAccountCard
            purpose="invoices"
            title="Invoices Email"
            description="Used when sending invoices to customers."
          />

          <h3
            id="email-import"
            ref={importSectionRef}
            className={styles.sectionHead}
          >
            Booking Import (Inbound Email)
          </h3>
          <EmailAccountCard
            purpose="import"
            title="Import Inbox"
            description="Connect your booking enquiry inbox. Emails from this inbox can be imported directly into a new booking."
          />
          <ImportParsersTable />

          <h3 className={styles.sectionHead}>SMS Configuration</h3>
          <SmsConfigCard />

          <h3 className={styles.sectionHead}>Communication Preferences</h3>
          <NotificationPreferencesGrid />
        </div>
      ) : (
      <Card>
        <form onSubmit={handleSubmit} className={styles.form}>
          {tab === 'info' ? (
            <>
              <div className={styles.row}>
                <Input
                  label="Company Name"
                  value={form.name ?? ''}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                />
                <Input
                  label="Email"
                  variant="email"
                  value={form.email ?? ''}
                  onChange={(e) => setField('email', e.target.value || null)}
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="Phone"
                  variant="tel"
                  value={form.phone ?? ''}
                  onChange={(e) => setField('phone', e.target.value || null)}
                />
                <Input
                  label="Postcode"
                  value={form.postcode ?? ''}
                  onChange={(e) => setField('postcode', e.target.value || null)}
                />
              </div>
              <Textarea
                label="Address"
                value={form.address ?? ''}
                onChange={(e) => setField('address', e.target.value || null)}
                rows={3}
              />
              <Input
                label="Website"
                value={form.website ?? ''}
                onChange={(e) => setField('website', e.target.value || null)}
                helper="Full URL, e.g. https://yourcompany.com"
              />
              <Input
                label="WhatsApp Number"
                value={form.whatsapp ?? ''}
                onChange={(e) => setField('whatsapp', e.target.value || null)}
                helper="Used for the {{company_whatsapp}} message variable. Leave blank if the same as phone."
              />
              <h3 className={styles.subhead}>Banking (used for BACS)</h3>
              <div className={styles.row}>
                <Input
                  label="Bank Name"
                  value={form.bank_name ?? ''}
                  onChange={(e) => setField('bank_name', e.target.value || null)}
                />
                <Input
                  label="Account Name"
                  value={form.bank_account_name ?? ''}
                  onChange={(e) => setField('bank_account_name', e.target.value || null)}
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="Sort Code"
                  value={form.bank_sort_code ?? ''}
                  onChange={(e) => setField('bank_sort_code', e.target.value || null)}
                  helper="Format: 12-34-56"
                />
                <Input
                  label="Account Number"
                  value={form.bank_account_number ?? ''}
                  onChange={(e) =>
                    setField('bank_account_number', e.target.value || null)
                  }
                  helper="6 to 8 digits"
                />
              </div>
            </>
          ) : null}

          {tab === 'branding' ? (
            <>
              <Input
                label="Logo URL"
                value={form.logo_url ?? ''}
                onChange={(e) => setField('logo_url', e.target.value || null)}
                helper="Public URL for the company logo. PNG/SVG recommended."
              />
              <div className={styles.row}>
                <Input
                  label="Primary Colour"
                  value={form.primary_color ?? ''}
                  onChange={(e) => setField('primary_color', e.target.value || null)}
                  helper="7-character hex (e.g. #2563eb)"
                />
                <Input
                  label="Secondary Colour"
                  value={form.secondary_color ?? ''}
                  onChange={(e) => setField('secondary_color', e.target.value || null)}
                  helper="7-character hex (e.g. #7c3aed)"
                />
              </div>
              <p className={styles.muted}>
                For the full theme picker, visit Settings → Theme.
              </p>
            </>
          ) : null}

          {tab === 'timeframes' ? (
            <>
              <p className={styles.muted}>
                These labels appear next to each booking slot in the diary and
                receipts. e.g. <em>“Morning collection (8am – 12pm)”</em>.
              </p>
              <div className={styles.row}>
                <Input
                  label="AM (Weekday)"
                  value={form.timeframe_am_weekday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_am_weekday', e.target.value)
                  }
                />
                <Input
                  label="AM (Saturday)"
                  value={form.timeframe_am_saturday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_am_saturday', e.target.value)
                  }
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="AM-PM (Weekday)"
                  value={form.timeframe_ampm_weekday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_ampm_weekday', e.target.value)
                  }
                />
                <Input
                  label="AM-PM (Saturday)"
                  value={form.timeframe_ampm_saturday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_ampm_saturday', e.target.value)
                  }
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="PM (Weekday)"
                  value={form.timeframe_pm_weekday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_pm_weekday', e.target.value)
                  }
                />
                <Input
                  label="PM (Saturday)"
                  value={form.timeframe_pm_saturday ?? ''}
                  onChange={(e) =>
                    setField('timeframe_pm_saturday', e.target.value)
                  }
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="Operating Hours (Weekdays)"
                  value={form.operating_hours_weekdays ?? ''}
                  onChange={(e) =>
                    setField('operating_hours_weekdays', e.target.value || null)
                  }
                  helper="e.g. 8:00 AM – 6:00 PM"
                />
                <Input
                  label="Operating Hours (Weekends)"
                  value={form.operating_hours_weekends ?? ''}
                  onChange={(e) =>
                    setField('operating_hours_weekends', e.target.value || null)
                  }
                  helper="e.g. 9:00 AM – 4:00 PM"
                />
              </div>
            </>
          ) : null}

          {tab === 'reminders' ? (
            <>
              <h3 className={styles.subhead}>Email reminder</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.automated_email_reminder_enabled ?? false}
                  onChange={(e) =>
                    setField('automated_email_reminder_enabled', e.target.checked)
                  }
                />
                <span>Send automated email reminder before collection</span>
              </label>
              <div className={styles.row}>
                <Input
                  label="Hours Before"
                  variant="number"
                  min={1}
                  max={720}
                  value={form.automated_email_reminder_hours ?? 24}
                  onChange={(e) =>
                    setField(
                      'automated_email_reminder_hours',
                      Number(e.target.value),
                    )
                  }
                />
                <Input
                  label="Time of Day"
                  value={form.automated_email_reminder_time ?? '09:00'}
                  onChange={(e) =>
                    setField('automated_email_reminder_time', e.target.value)
                  }
                  helper="HH:MM 24-hour, e.g. 09:00"
                />
              </div>

              <h3 className={styles.subhead}>SMS reminder</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.automated_sms_reminder_enabled ?? false}
                  onChange={(e) =>
                    setField('automated_sms_reminder_enabled', e.target.checked)
                  }
                />
                <span>Send automated SMS reminder before collection</span>
              </label>
              <div className={styles.row}>
                <Input
                  label="Hours Before"
                  variant="number"
                  min={1}
                  max={720}
                  value={form.automated_sms_reminder_hours ?? 24}
                  onChange={(e) =>
                    setField(
                      'automated_sms_reminder_hours',
                      Number(e.target.value),
                    )
                  }
                />
                <Input
                  label="Time of Day"
                  value={form.automated_sms_reminder_time ?? '09:00'}
                  onChange={(e) =>
                    setField('automated_sms_reminder_time', e.target.value)
                  }
                  helper="HH:MM 24-hour, e.g. 09:00"
                />
              </div>
            </>
          ) : null}

          <div className={styles.footer}>
            <Button type="submit" variant="primary" icon={Save} loading={update.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
      )}
    </div>
  );
}
